import React, { useState, useEffect } from 'react';
import CustomTextInput from '../InputFields/CustomTextInput';
import Dropdown from '@/components/Dropdown/Dropdown';
import CustomToggle from '../InputFields/CustomToggel';
import Button from '@/components/Button/Button';
import { v4 as uuidv4 } from 'uuid';

const FieldOptions = [
  { value: 'text_input', label: 'Text input' },
  { value: 'text_area', label: 'Text Area' },
  { value: 'file', label: 'File Upload' },
  { value: 'no_action', label: 'No Action' },
];

interface Field {
  id: string;
  label?: string;
  field_type: string;
  required?: boolean;
  [key: string]: any;
}

interface Option {
  id: string;
  name: string;
  fields: Field[];
}

interface EditOptionProps {
  mode: 'add' | 'edit'; // 'add' for new option, 'edit' for editing existing
  option?: Option; // present in edit mode
  onSave: (updatedOption: Option) => void;
  onCancel: () => void;
}

// This component supports both add and edit modes for options
const EditOption: React.FC<EditOptionProps> = ({ mode, option, onSave, onCancel }) => {
  const defaultField = { id: uuidv4(), label: '', field_type: '', required: false };
  const defaultOption: Option = {
    id: uuidv4(),
    name: '',
    fields: [defaultField],
  };
  const [optionState, setOptionState] = useState<Option>(
    mode === 'edit' && option ? { ...option } : defaultOption
  );
  const [error, setError] = useState('');

  useEffect(() => {
    if (mode === 'edit' && option) {
      setOptionState({ ...option });
    } else if (mode === 'add') {
      setOptionState(defaultOption);
    }
    setError('');
  }, [mode, option]);

  const handleLabelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOptionState((prev) => ({ ...prev, name: e.target.value }));
    setError('');
  };

  const handleFieldChange = (fieldIdx: number, key: string, value: any) => {
    setOptionState((prev) => ({
      ...prev,
      fields: prev.fields.map((f, idx) => (idx === fieldIdx ? { ...f, [key]: value } : f)),
    }));
  };

  const handleAddField = () => {
    setOptionState((prev) => ({
      ...prev,
      fields: [...prev.fields, { id: uuidv4(), label: '', field_type: '', required: false }],
    }));
  };

  const handleRemoveField = (fieldIdx: number) => {
    setOptionState((prev) => ({
      ...prev,
      fields:
        prev.fields.length > 1 ? prev.fields.filter((_, idx) => idx !== fieldIdx) : prev.fields,
    }));
  };

  const handleSave = () => {
    if (!optionState?.name?.trim()) {
      setError('Option label is required');
      return;
    }
    for (const field of optionState.fields) {
      if (!field.label || !field.label.trim()) {
        setError('All fields must have a label');
        return;
      }
      if (!field.field_type) {
        setError('All fields must have a type');
        return;
      }
    }
    setError('');
    onSave(optionState);
  };

  return (
    <div className="p-4 w-full max-w-lg mx-auto">
      <h2 className="text-lg font-semibold mb-4">
        {mode === 'add' ? 'Add Option' : 'Edit Option'}
      </h2>
      <CustomTextInput
        label="Option Name"
        name="optionLabel"
        value={optionState.name}
        onChange={handleLabelChange}
        required
        error={error}
        description="This will show up as the label of the option."
      />
      <div className="mt-4">
        <label className="text-xs text-ds-gray-500 font-semibold mb-2 block">Fields</label>
        {optionState.fields.map((field, idx) => (
          <div key={field.id} className="mb-3 p-2 border rounded">
            <CustomTextInput
              label="Field Label"
              name={`fieldLabel-${field.id}`}
              value={field.label || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleFieldChange(idx, 'label', e.target.value)
              }
              required
            />
            <Dropdown
              options={FieldOptions}
              selectedValue={field.field_type}
              onChange={(val: string) => handleFieldChange(idx, 'field_type', val)}
              error={!field.field_type}
              errorMessage="Field type is required"
            />
            <CustomToggle
              label="Required"
              checked={field.required ?? false}
              onChange={(val: boolean) => handleFieldChange(idx, 'required', val)}
            />
            {optionState.fields.length > 1 && (
              <Button color="outlined" size="sm" onClick={() => handleRemoveField(idx)}>
                Remove Field
              </Button>
            )}
          </div>
        ))}
        <Button color="primary" size="sm" onClick={handleAddField}>
          Add Field
        </Button>
      </div>
      <div className="flex gap-2 mt-4">
        <Button color="primary" onClick={handleSave}>
          {mode === 'add' ? 'Add' : 'Save'}
        </Button>
        <Button color="outlined" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default EditOption;
